import { REST_GAZE, mapByGaze, unmapByGaze, type HeadGaze } from './face'
import { TAU, clamp, easings, lerp, r2 } from './math'
import { profileFromPolygon, blend, closedPath, toPoints, type Point, type Silhouette } from './shape'
import { profileWaist } from './shapeEyes'
import { SPARKLE_STAR_RADII, SHAPE_BY_ID } from './skins'

/**
 * Losi logotype geometry (viewBox 100×100) — orbit decor and logo separate.
 * The star-shaped bot body reuses the radial profile of the large sparkle.
 */

/** Large sparkle — exact paths from the React component, viewBox 100. */
export const LOIS_SPARKLE_LG_100 =
  'M50 35C50 35 51.2 40.5 55.75 45.25C60.5 50 66 51.25 66 51.25C66 51.25 60.5 52.5 55.75 57.25C51.2 62 50 67.5 50 67.5C50 67.5 48.8 62 44.25 57.25C39.5 52.5 34 51.25 34 51.25C34 51.25 39.5 50 44.25 45.25C48.8 40.5 50 35 50 35Z'

/** Small satellite sparkle — exact paths, viewBox 100. */
export const LOIS_SPARKLE_SM_100 =
  'M76.8 14C76.8 14 77.42 16.58 79.4 18.6C81.42 20.62 84 21.2 84 21.2C84 21.2 81.42 21.78 79.4 23.8C77.42 25.82 76.8 28.4 76.8 28.4C76.8 28.4 76.18 25.82 74.2 23.8C72.18 21.78 69.6 21.2 69.6 21.2C69.6 21.2 72.18 20.62 74.2 18.6C76.18 16.58 76.8 14 76.8 14Z'

/** Small sparkle centred on the origin, for the satellite orbiting the bot. */
export const LOIS_SPARKLE_SM =
  'M0 -7.2C0 -7.2 0.62 -4.62 2.6 -2.6C4.62 -0.58 7.2 0 7.2 0C7.2 0 4.62 0.58 2.6 2.6C0.62 4.62 0 7.2 0 7.2C0 7.2 -0.62 4.62 -2.6 2.6C-4.62 0.58 -7.2 0 -7.2 0C-7.2 0 -4.62 -0.58 -2.6 -2.6C-0.62 -4.62 0 -7.2 0 -7.2Z'

/** Samples a closed cubic SVG path into a polygon. */
function sampleCubicPath(d: string, steps = 28): Point[] {
  const pts: Point[] = []
  let cx = 0
  let cy = 0
  for (const tok of d.trim().split(/(?=[MCZ])/i)) {
    const cmd = tok[0]
    const nums = tok
      .slice(1)
      .trim()
      .split(/[\s,]+/)
      .map(Number)
      .filter((n) => !Number.isNaN(n))
    if (cmd === 'M') {
      cx = nums[0]!
      cy = nums[1]!
      pts.push({ x: cx, y: cy })
    } else if (cmd === 'C') {
      for (let j = 0; j + 5 < nums.length; j += 6) {
        const p0 = { x: cx, y: cy }
        const p1 = { x: nums[j]!, y: nums[j + 1]! }
        const p2 = { x: nums[j + 2]!, y: nums[j + 3]! }
        const p3 = { x: nums[j + 4]!, y: nums[j + 5]! }
        for (let s = 1; s <= steps; s++) {
          const t = s / steps
          const u = 1 - t
          pts.push({
            x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x,
            y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y
          })
        }
        cx = p3.x
        cy = p3.y
      }
    }
  }
  return pts
}

const SPARKLE_CENTER = 50

let _losiSparkleRadii: number[] | null = null

/** Radial profile of the large sparkle, centred at the origin and normalised to 1. */
export function losiSparkleProfile(): number[] {
  if (_losiSparkleRadii) return _losiSparkleRadii
  const poly = sampleCubicPath(LOIS_SPARKLE_LG_100)
  const raw = profileFromPolygon(poly, SPARKLE_CENTER, SPARKLE_CENTER)
  const peak = Math.max(...raw)
  _losiSparkleRadii = raw.map((r) => r / peak)
  return _losiSparkleRadii
}

export const LOIS_PERIOD = 4

export const LOIS_ORBIT_RX = 26.6
export const LOIS_ORBIT_RY = 41.3
export const LOIS_ORBIT_ROT = -44.3491
export const LOIS_RING_STROKE = 4.5

/** Logotype large sparkle width in viewBox 100 (path bbox, 34→66). */
export const LOIS_SPARKLE_LG_WIDTH = 32

/** Logotype small sparkle width in viewBox 100 (path bbox, 69.6→84). */
export const LOIS_SPARKLE_SM_WIDTH = 14.4

/** Centred bot satellite path width (`LOIS_SPARKLE_SM` bbox, −7.2→7.2). */
export const LOIS_SM_PATH_WIDTH = 14.4

/**
 * `sparkleStar` scale for sleep — logotype planet size, bumped for the star waist
 * (the radial profile reads smaller than the SVG path bbox).
 */
export const SLEEP_SPARKLE_SCALE = 0.38

/**
 * Soft planet pulse around sleep size (±~10%). The old Lottie 143% peak and
 * satellite-sized floor read too loud for the logotype.
 */
export const LOGO_LG_PEAK = 1.1
export const LOGO_LG_FLOOR = 0.9

/** Outer sparkle half-size in ball-radius units. */
export const LOGO_SATELLITE_SCALE = LOIS_SPARKLE_SM_WIDTH / 2 / 50

/** Largest logo planet — a gentle swell above sleep. */
export const LOGO_PLANET_MAX = SLEEP_SPARKLE_SCALE * LOGO_LG_PEAK

/** Smallest logo planet — a gentle shrink, still well above the satellite. */
export const LOGO_PLANET_MIN = SLEEP_SPARKLE_SCALE * LOGO_LG_FLOOR

/** Peak orbit-ring skew — kept at 0; the oval stays still. */
export const LOGO_RING_SKEW_PEAK = 0

/**
 * Inhale weight: 1 at the large peaks, 0 at the trough.
 * Pure cosine — same continuous breath as sleep, no ease that holds the extremes.
 */
export function logoCurve(t: number): number {
  const u = (t % LOIS_PERIOD) / LOIS_PERIOD
  return (1 + Math.cos(u * TAU)) / 2
}

/** Absolute planet scale: big → small → big, locked to `logoCurve`. */
export function logoPlanetScale(t: number): number {
  return LOGO_PLANET_MIN + (LOGO_PLANET_MAX - LOGO_PLANET_MIN) * logoCurve(t)
}

/**
 * SVG / relative scale vs the rest logotype path (`SLEEP_SPARKLE_SCALE` → 1).
 */
export function logoLgScale(t: number): number {
  return logoPlanetScale(t) / SLEEP_SPARKLE_SCALE
}

/** @deprecated alias of `logoCurve`. */
export function logoGrow(t: number): number {
  return logoCurve(t)
}

/**
 * Shape tracks size 1:1 on the same sine: sleep star when big, logotype when small.
 * No cubic gate — that held the ends and rushed the middle.
 */
export function logoSharpen(t: number): number {
  return 1 - logoCurve(t)
}

/** Oval does not move. */
export function logoRingSkew(_t: number): number {
  return 0
}
/** Satellite render scale in ball units, matched to the logotype SM path. */
export function losiSatelliteScale(R: number): number {
  return (LOIS_SPARKLE_SM_WIDTH / LOIS_SM_PATH_WIDTH) * (R / 50)
}

/** Logo SM path anchor in viewBox 100 — resting satellite sits here in the logotype. */
const LOGO_SM_ANCHOR = { x: 77.2, y: 21.2 }
const LOGO_CENTER = { x: 50, y: 50 }

/** Point on the Losi orbit ellipse, in viewBox 100 coordinates. */
export function pointOnLosiOrbit(angle: number, cx = 50, cy = 50) {
  const rot = (LOIS_ORBIT_ROT * Math.PI) / 180
  const x0 = LOIS_ORBIT_RX * Math.cos(angle)
  const y0 = LOIS_ORBIT_RY * Math.sin(angle)
  return {
    x: cx + x0 * Math.cos(rot) - y0 * Math.sin(rot),
    y: cy + x0 * Math.sin(rot) + y0 * Math.cos(rot)
  }
}

function restingOrbitFromLogo(): number {
  let angle = 0
  let bestDist = Infinity
  for (let i = 0; i <= 4000; i++) {
    const a = (i / 4000) * TAU
    const p = pointOnLosiOrbit(a, LOGO_CENTER.x, LOGO_CENTER.y)
    const d = Math.hypot(p.x - LOGO_SM_ANCHOR.x, p.y - LOGO_SM_ANCHOR.y)
    if (d < bestDist) {
      bestDist = d
      angle = a
    }
  }
  return angle
}

/** Parametric angle whose point aims straight up on screen (12 o'clock). */
function orbitTopFromLogo(): number {
  let angle = 0
  let best = -Infinity
  for (let i = 0; i <= 4000; i++) {
    const a = (i / 4000) * TAU
    const p = pointOnLosiOrbit(a, LOGO_CENTER.x, LOGO_CENTER.y)
    const dx = p.x - LOGO_CENTER.x
    const dy = p.y - LOGO_CENTER.y
    const score = -dy / Math.hypot(dx, dy)
    if (score > best) {
      best = score
      angle = a
    }
  }
  return angle
}

/** Orbit angle nearest the static logotype satellite (upper-right, ≈1–2 o'clock). */
export const RESTING_ORBIT = restingOrbitFromLogo()

/** Screen-up (90°) start of the logo animate-in. */
export const ORBIT_TOP = orbitTopFromLogo()

/** Animate-in: top → top-right rest (short arc, ease-out). */
export const LOGO_INTRO = 0.9

/** Satellite path scale so the outer rest matches the logotype corner. */
export const OUTER_ORBIT_SCALE = (() => {
  const on = pointOnLosiOrbit(RESTING_ORBIT, LOGO_CENTER.x, LOGO_CENTER.y)
  const rOn = Math.hypot(on.x - LOGO_CENTER.x, on.y - LOGO_CENTER.y)
  const rAnchor = Math.hypot(LOGO_SM_ANCHOR.x - LOGO_CENTER.x, LOGO_SM_ANCHOR.y - LOGO_CENTER.y)
  return rAnchor / rOn
})()

/** Angle along the short CCW arc from 12 o'clock to the top-right rest. */
function orbitAngleIntro(t: number): number {
  const span = (RESTING_ORBIT - ORBIT_TOP + TAU) % TAU
  return ORBIT_TOP + easings.easeOutCubic(clamp(t / LOGO_INTRO)) * span
}

/** Continuous revolution phased so t = 0 sits at 12 o'clock. */
function orbitAngleLoop(t: number): number {
  return ORBIT_TOP + ((t % LOIS_PERIOD) / LOIS_PERIOD) * TAU
}

function scaleFromCenter(p: { x: number; y: number }, k: number) {
  return {
    x: r2(LOGO_CENTER.x + (p.x - LOGO_CENTER.x) * k),
    y: r2(LOGO_CENTER.y + (p.y - LOGO_CENTER.y) * k)
  }
}

export interface LosiLogoFrame {
  /** Planet path in viewBox 100 — already sized; do not scale again. */
  planetPath: string
  /** Orbit ring skew in degrees (Lottie). */
  ringSkew: number
  /** 1 = fully curved, 0 = fully sharp (after shrink). */
  morph: number
  sm: { x: number; y: number }
}

/**
 * Animated logo: breathing planet + satellite outside the oval.
 * - default: animate-in from 12 o'clock, settle at the real top-right corner
 * - `outer`: keep revolving outside (glitter opener)
 * Planet: big = sleep sparkle ⇄ small = sharp logotype. Oval stays still.
 */
export function sampleLosiLogo(t: number, outer = false): LosiLogoFrame {
  const angle = outer || t < LOGO_INTRO ? (outer ? orbitAngleLoop(t) : orbitAngleIntro(t)) : RESTING_ORBIT
  const on = pointOnLosiOrbit(angle, LOGO_CENTER.x, LOGO_CENTER.y)
  const sm =
    !outer && t >= LOGO_INTRO
      ? { x: r2(LOGO_SM_ANCHOR.x), y: r2(LOGO_SM_ANCHOR.y) }
      : scaleFromCenter(on, OUTER_ORBIT_SCALE)
  return {
    planetPath: logoPlanetPath(t),
    ringSkew: 0,
    morph: 1 - logoSharpen(t),
    sm
  }
}

/** Point on the Losi orbit in ball-radius units (planet centred on the origin). */
export function losiOrbitBall(angle: number) {
  const p = pointOnLosiOrbit(angle, 0, 0)
  const k = 1 / 50
  return { x: p.x * k, y: p.y * k }
}

/** Customiser sparkle star (`skins` etoile profile) at a given scale. */
export function sparkleStar(scale: number, pose: Partial<Silhouette> = {}): Silhouette {
  return {
    radii: SPARKLE_STAR_RADII.map((r) => r * scale),
    rot: 0,
    cx: 0,
    cy: 0,
    sx: 1,
    sy: 1,
    ...pose
  }
}

/**
 * Previous soft etoile planet, same bbox as `LOIS_SPARKLE_LG_100` — morph source
 * for the logo big-sparkle cross-fade in `<LosiLogo />`.
 */
export const SOFT_SPARKLE_LG_100 = (() => {
  const pts = toPoints(sparkleStar(1), LOIS_SPARKLE_LG_WIDTH / 2)
  for (const p of pts) {
    p.x += 50
    p.y += 50
  }
  return closedPath(pts)
})()

/**
 * Crisp logotype planet — full waist, same family as the outer satellite.
 */
export function logoSparkle(scale: number, _curve = 0, pose: Partial<Silhouette> = {}): Silhouette {
  return {
    radii: losiSparkleProfile().map((r) => r * scale),
    rot: 0,
    cx: 0,
    cy: 0,
    sx: 1,
    sy: 1,
    ...pose
  }
}

/**
 * Planet breath: size + shape ride the same sine (sleep star ⇄ sharp logotype).
 * Oval + satellite stay up via `showUniverse` on logo states.
 */
export function logoPlanetSilhouette(t: number): Silhouette {
  const scale = logoPlanetScale(t)
  const sharpen = logoSharpen(t)
  return blend(sparkleStar(scale), logoSparkle(scale), sharpen)
}

/** ViewBox-100 path for the morphing logo planet (single shape, no dual fade). */
export function logoPlanetPath(t: number): string {
  const pts = toPoints(logoPlanetSilhouette(t), 50)
  for (const p of pts) {
    p.x += 50
    p.y += 50
  }
  return closedPath(pts)
}

/** True when the silhouette is the four-lobe sparkle, not a plain circle or other shape. */
export function isSparkleSilhouette(sil: Silhouette): boolean {
  const peak = Math.max(...sil.radii)
  if (peak < 0.08) return false
  return profileWaist(sil.radii) < 0.75
}

function peakNorm(radii: number[]): number[] {
  const peak = Math.max(...radii)
  if (peak <= 0) return radii
  return radii.map((r) => r / peak)
}

function rmsErr(a: number[], b: number[]): number {
  const n = Math.min(a.length, b.length)
  if (n === 0) return 1
  let e = 0
  for (let i = 0; i < n; i++) {
    const d = a[i]! - b[i]!
    e += d * d
  }
  return Math.sqrt(e / n)
}

function smoothstep(e0: number, e1: number, x: number): number {
  const t = clamp((x - e0) / (e1 - e0))
  return t * t * (3 - 2 * t)
}

const STAR_TEMPLATES: number[][] = []

function starTemplates(): number[][] {
  if (STAR_TEMPLATES.length === 0) {
    STAR_TEMPLATES.push(peakNorm(SPARKLE_STAR_RADII), peakNorm(losiSparkleProfile()))
  }
  return STAR_TEMPLATES
}

/**
 * How much this profile is the four-lobe star (etoile / logotype sparkle).
 * 1 on those two and their blends; 0 on the diamond, triangle, circle, …
 * Waist rejects the circle (cosine similarity cannot). Smooth so a shape
 * morph off the star does not pop the 3D seating.
 */
export function starBodyAmount(radii: number[]): number {
  const peak = Math.max(...radii)
  if (peak < 0.08) return 0
  if (Math.min(...radii) / peak > 0.7) return 0
  const n = peakNorm(radii)
  let best = Infinity
  for (const tmpl of starTemplates()) best = Math.min(best, rmsErr(n, tmpl))
  return 1 - smoothstep(0.015, 0.07, best)
}

/** True when the silhouette should sit on the eye sphere. */
export function isStarBody(sil: Silhouette): boolean {
  return starBodyAmount(sil.radii) > 0.5
}

const ROUND_TEMPLATES: number[][] = []

function roundTemplates(): number[][] {
  if (ROUND_TEMPLATES.length === 0) {
    const planet = SHAPE_BY_ID.get('jovi')?.radii
    if (planet) ROUND_TEMPLATES.push(peakNorm(planet))
  }
  return ROUND_TEMPLATES
}

/**
 * How much this profile is the catalogue planet (round disc + ground scallops).
 * Smooth across a shape morph so volume seating does not pop.
 */
export function roundBodyAmount(radii: number[]): number {
  const tmpls = roundTemplates()
  if (tmpls.length === 0) return 0
  const peak = Math.max(...radii)
  if (peak < 0.08) return 0
  const n = peakNorm(radii)
  let best = Infinity
  for (const tmpl of tmpls) best = Math.min(best, rmsErr(n, tmpl))
  return 1 - smoothstep(0.02, 0.09, best)
}

const PETA_TEMPLATES: number[][] = []

function petaTemplates(): number[][] {
  if (PETA_TEMPLATES.length === 0) {
    const peta = SHAPE_BY_ID.get('peta')?.radii
    if (peta) PETA_TEMPLATES.push(peakNorm(peta))
  }
  return PETA_TEMPLATES
}

/**
 * How much this profile is catalogue Peta (five soft lobes). Smooth across a
 * shape morph so volume seating does not pop.
 */
export function petaBodyAmount(radii: number[]): number {
  const tmpls = petaTemplates()
  if (tmpls.length === 0) return 0
  const peak = Math.max(...radii)
  if (peak < 0.08) return 0
  // Reject near-circles and the four-lobe star — different glyphs.
  if (Math.min(...radii) / peak > 0.88) return 0
  if (starBodyAmount(radii) > 0.4) return 0
  const n = peakNorm(radii)
  let best = Infinity
  for (const tmpl of tmpls) best = Math.min(best, rmsErr(n, tmpl))
  return 1 - smoothstep(0.02, 0.09, best)
}

export function fadeUniverse(u: UniverseDecor, k: number): UniverseDecor {
  return {
    ring: { ...u.ring, opacity: u.ring.opacity * k },
    satellite: { ...u.satellite, opacity: u.satellite.opacity * k },
    trails: u.trails.map((trail) => ({ ...trail, opacity: trail.opacity * k }))
  }
}

/** Shortest-path lerp for sparkle tip angles (degrees). */
function lerpDeg(a: number, b: number, t: number): number {
  let d = b - a
  while (d > 180) d -= 360
  while (d < -180) d += 360
  return a + d * t
}

/**
 * Tip tumble from orbit phase, in degrees. Zero at the logotype park angle so
 * the resting satellite stays upright like the static logo.
 */
export function sparkleOrbitTwistDeg(orbit: number): number {
  let d = orbit - RESTING_ORBIT
  while (d > Math.PI) d -= TAU
  while (d < -Math.PI) d += TAU
  return (d * 180) / Math.PI
}

/**
 * Extra tip twist from head turn (degrees). Zero at `REST_GAZE`.
 * Uses the change in screen bearing of this sparkle so tips angle with the
 * sphere instead of staying billboard-upright while only the centre moves.
 */
export function sparkleBearingTwistDeg(
  x: number,
  y: number,
  gaze: HeadGaze
): number {
  const local = unmapByGaze([x, y, 0], REST_GAZE)
  const [wx, wy] = mapByGaze(local, gaze)
  let d = Math.atan2(wy, wx) - Math.atan2(y, x)
  while (d > Math.PI) d -= TAU
  while (d < -Math.PI) d += TAU
  return (d * 180) / Math.PI
}

/** Cross-fade orbit decor during a state morph (sleep ↔ orbiting sparkle). */
export function blendUniverse(a: UniverseDecor, b: UniverseDecor, k: number): UniverseDecor {
  const trailCount = Math.max(a.trails.length, b.trails.length)
  const trails = Array.from({ length: trailCount }, (_, i) => {
    const za = a.trails[i]
    const zb = b.trails[i]
    const fa = za ?? { x: zb!.x, y: zb!.y, scale: 0, opacity: 0, rot: zb!.rot }
    const fb = zb ?? { x: za!.x, y: za!.y, scale: 0, opacity: 0, rot: za!.rot }
    return {
      x: r2(lerp(fa.x, fb.x, k)),
      y: r2(lerp(fa.y, fb.y, k)),
      scale: r2(lerp(fa.scale, fb.scale, k)),
      opacity: r2(lerp(fa.opacity, fb.opacity, k)),
      rot: r2(lerpDeg(fa.rot ?? 0, fb.rot ?? 0, k))
    }
  })
  return {
    ring: {
      rx: r2(lerp(a.ring.rx, b.ring.rx, k)),
      ry: r2(lerp(a.ring.ry, b.ring.ry, k)),
      rot: a.ring.rot,
      stroke: r2(lerp(a.ring.stroke, b.ring.stroke, k)),
      opacity: r2(lerp(a.ring.opacity, b.ring.opacity, k)),
      skew: r2(lerp(a.ring.skew, b.ring.skew, k))
    },
    satellite: {
      x: r2(lerp(a.satellite.x, b.satellite.x, k)),
      y: r2(lerp(a.satellite.y, b.satellite.y, k)),
      scale: r2(lerp(a.satellite.scale, b.satellite.scale, k)),
      opacity: r2(lerp(a.satellite.opacity, b.satellite.opacity, k)),
      rot: r2(lerpDeg(a.satellite.rot ?? 0, b.satellite.rot ?? 0, k))
    },
    trails
  }
}

export interface UniverseTrail {
  x: number
  y: number
  scale: number
  opacity: number
  /** Tip angle in degrees — tumbles with orbit / head; 0 = logo-upright. */
  rot: number
  /** True when the sparkle is on the far side of the sphere (draw behind the body). */
  behind?: boolean
}

/** « Universe » decor around the bot: ring + satellite, in units of radius R. */
export interface UniverseDecor {
  ring: {
    rx: number
    ry: number
    rot: number
    stroke: number
    opacity: number
    skew: number
    cx?: number
    cy?: number
  }
  satellite: {
    x: number
    y: number
    scale: number
    opacity: number
    /** Tip angle in degrees — tumbles with orbit / head; 0 = logo-upright. */
    rot: number
    behind?: boolean
  }
  trails: UniverseTrail[]
}

/**
 * Seat the logotype sparkle on the same sphere as the eyes.
 *
 * The 2D ellipse layout is the rest pose. Unmapping it through `REST_GAZE` and
 * remapping through the live gaze makes the satellite turn, foreshorten and
 * pass behind the body — it was a screen-space sticker before.
 */
export function seatUniverseOnGaze(
  u: UniverseDecor,
  gaze: HeadGaze,
  origin: { x: number; y: number } = { x: 0, y: 0 }
): UniverseDecor {
  const seat = (
    x: number,
    y: number,
    scale: number,
    opacity: number,
    rot: number
  ): { x: number; y: number; scale: number; opacity: number; rot: number; behind: boolean } => {
    const local = unmapByGaze([x, y, 0], REST_GAZE)
    const [wx, wy, wz] = mapByGaze(local, gaze)
    const r = Math.hypot(x, y) || 1
    const depth = clamp(wz / r, -1, 1)
    // Rest sits at z = 0, so this is 1 there. Front grows, back shrinks.
    return {
      x: r2(wx + origin.x),
      y: r2(wy + origin.y),
      scale: r2(scale * (1 + 0.22 * depth)),
      opacity,
      // Orbit tumble + bearing on the sphere — tips were billboarded upright before.
      rot: r2(rot + sparkleBearingTwistDeg(x, y, gaze)),
      behind: wz < 0
    }
  }

  return {
    ring: {
      ...u.ring,
      cx: r2(origin.x),
      cy: r2(origin.y),
      rot: u.ring.rot + (gaze.roll - REST_GAZE.roll)
    },
    satellite: {
      ...u.satellite,
      ...seat(
        u.satellite.x,
        u.satellite.y,
        u.satellite.scale,
        u.satellite.opacity,
        u.satellite.rot ?? 0
      )
    },
    trails: u.trails.map((trail) => ({
      ...trail,
      ...seat(trail.x, trail.y, trail.scale, trail.opacity, trail.rot ?? 0)
    }))
  }
}

export function sampleUniverse(t: number, R: number): UniverseDecor {
  const orbit = (t % LOIS_PERIOD) / LOIS_PERIOD * TAU + RESTING_ORBIT
  return buildUniverse(orbit, R, { trails: true, pulse: true })
}

/** Logo: satellite outside the oval — animate-in from 12 o'clock to the real corner. */
export function sampleUniverseLogo(t: number, R: number): UniverseDecor {
  const parked = t >= LOGO_INTRO
  const angle = orbitAngleIntro(t)
  const u = buildUniverse(angle, R, { trails: !parked, pulse: true, skew: 0 })
  const k = OUTER_ORBIT_SCALE
  const scaleK = R / 50
  if (parked) {
    return {
      ...u,
      satellite: {
        ...u.satellite,
        x: r2((LOGO_SM_ANCHOR.x - LOGO_CENTER.x) * scaleK),
        y: r2((LOGO_SM_ANCHOR.y - LOGO_CENTER.y) * scaleK),
        // Match the static logotype — tips upright at the park corner.
        rot: 0
      },
      trails: []
    }
  }
  return {
    ...u,
    satellite: { ...u.satellite, x: r2(u.satellite.x * k), y: r2(u.satellite.y * k) },
    trails: u.trails.map((trail) => ({
      ...trail,
      x: r2(trail.x * k),
      y: r2(trail.y * k)
    }))
  }
}

/**
 * Same ring as the logotype, satellite on a larger ellipse so it revolves
 * *outside* the oval (the « glitter » logo opener look). Phased from 12 o'clock;
 * the rest angle lands on the real logotype corner.
 */
export function sampleUniverseOuter(t: number, R: number): UniverseDecor {
  const u = buildUniverse(orbitAngleLoop(t), R, {
    trails: true,
    pulse: true,
    skew: 0
  })
  const k = OUTER_ORBIT_SCALE
  return {
    ...u,
    satellite: { ...u.satellite, x: r2(u.satellite.x * k), y: r2(u.satellite.y * k) },
    trails: u.trails.map((trail) => ({
      ...trail,
      x: r2(trail.x * k),
      y: r2(trail.y * k)
    }))
  }
}

/** Sleep: ring + satellite parked at the logotype anchor, no trail ghosts. */
export function sampleUniverseResting(t: number, R: number): UniverseDecor {
  const breath = 1 + 0.06 * Math.sin(((t % LOIS_PERIOD) / LOIS_PERIOD) * TAU)
  const k = R / 50
  const u = buildUniverse(RESTING_ORBIT, R, { trails: false, pulse: false })
  return {
    ...u,
    ring: { ...u.ring, opacity: 0.55 },
    satellite: {
      ...u.satellite,
      x: r2((LOGO_SM_ANCHOR.x - LOGO_CENTER.x) * k),
      y: r2((LOGO_SM_ANCHOR.y - LOGO_CENTER.y) * k),
      opacity: 0.36 + 0.1 * breath,
      rot: 0
    }
  }
}

function buildUniverse(
  orbit: number,
  R: number,
  opts: { trails: boolean; pulse: boolean; skew?: number }
): UniverseDecor {
  const k = R / 50
  const satScale = losiSatelliteScale(R)
  const satellite = pointOnLosiOrbit(orbit, 0, 0)
  const trails = opts.trails
    ? [0.22, 0.44].map((lag) => {
        const p = pointOnLosiOrbit(orbit - lag, 0, 0)
        return {
          x: r2(p.x * k),
          y: r2(p.y * k),
          scale: r2(satScale * (1 - lag * 0.6)),
          opacity: r2(0.45 * (1 - lag)),
          rot: r2(sparkleOrbitTwistDeg(orbit - lag))
        }
      })
    : []

  return {
    ring: {
      rx: r2(LOIS_ORBIT_RX * k),
      ry: r2(LOIS_ORBIT_RY * k),
      rot: LOIS_ORBIT_ROT,
      stroke: r2((LOIS_RING_STROKE / 100) * R),
      opacity: 0.84,
      skew: opts.skew ?? 0
    },
    satellite: {
      x: r2(satellite.x * k),
      y: r2(satellite.y * k),
      scale: r2(satScale),
      opacity: opts.pulse ? 0.92 + 0.08 * Math.sin(orbit * 2) : 0.85,
      rot: r2(sparkleOrbitTwistDeg(orbit))
    },
    trails
  }
}
